/**
 * Every page address of the website. The router, links, redirects and tests use these names,
 * never a typed string, so renaming a page is one change here.
 */
export const pageRoutes = {
  dashboard: '/',
  imports: '/imports',
  newImport: '/imports/new',
  importDetail: '/imports/:importId',
  products: '/products',
  /** Step 3: imported products whose weight a person should look at. */
  review: '/review',
  /** Step 4: the products sent to the store, Dropbot and the store's availability. */
  publish: '/publish',
  /** The store's categories (its collections) and the category map. */
  categories: '/categories',
  fetchRuns: '/fetch',
  fetchRun: '/fetch/:runId',
  proxies: '/proxies',
  jobs: '/jobs',
  tester: '/tester',
  weightTable: '/weight-table',
  /** The products read on Ubuy with a weight, by category (the owner, 29 Oct 2026). */
  ubuyData: '/ubuy-data',
  /** The weight model learned from Ubuy's weights: the live one, every run, the judge's checks. */
  weightModel: '/weight-model',
  /** The store rules: clothes and shoes weighed without the AI (SK-97). */
  storeRules: '/store-rules',
  /** Every store product's weight checked (SK-95). The Sawakart products tester until 3 Nov 2026. */
  storeProductsTester: '/sawakart-products-weights',
  /** The products weights page's rough estimates beside the weights of the products with similar photos. */
  imageComparison: '/image-comparison',
  /** How a product's shipping weight is decided, A to H, with the latest run's counts. */
  howWeightsWork: '/how-weights-work',
  aiUsage: '/ai-usage',
  login: '/login',
} as const;

export type PageRoute = (typeof pageRoutes)[keyof typeof pageRoutes];

/**
 * Addresses a page no longer has. Links to them sit in Telegram messages, emails, Excel files and bookmarks,
 * so each stays and opens the page's address today, with its query string.
 */
export const oldPageRoutes = [
  { from: '/sawakart-products-tester', to: pageRoutes.storeProductsTester },
  /** The owner's own spelling when he asked for the new address (3 Nov 2026). */
  { from: '/sawakart-products-wieghts', to: pageRoutes.storeProductsTester },
] as const;

const id = (value: string) => encodeURIComponent(value);

/** Addresses with a value in them: one import, one run, a product opened over the Products list. */
export const pageLinks = {
  import: (importId: string) => pageRoutes.importDetail.replace(':importId', id(importId)),
  fetchRun: (runId: string) => pageRoutes.fetchRun.replace(':runId', id(runId)),
  product: (productId: string) => `${pageRoutes.products}?product=${id(productId)}`,
  /** The Products page searched for an ASIN. */
  productSearch: (asin: string) => `${pageRoutes.products}?search=${encodeURIComponent(asin)}`,
  /** One product opened on the Review page. */
  reviewProduct: (productId: string) => `${pageRoutes.review}?product=${id(productId)}`,
  /** One tab of the Review page. */
  reviewTab: (state: 'set' | 'check' | 'category' | 'dropbot') => `${pageRoutes.review}?state=${state}`,
  /** One product opened on the Review page's Dropbot tab (Dropbot can't sell it, or some of its variants). */
  reviewDropbot: (productId: string) => `${pageRoutes.review}?state=dropbot&product=${id(productId)}`,
  /** One row of the category weight table, opened. */
  weightTableRow: (key: string) => `${pageRoutes.weightTable}?row=${id(key)}`,
  /** One weight model, opened. */
  weightModel: (name: string) => `${pageRoutes.weightModel}?model=${id(name)}`,
  /** One Product tester result. */
  weightTest: (testId: string) => `${pageRoutes.tester}?test=${id(testId)}`,
  productsWithStatus: (status: 'ready' | 'rejected' | 'pending') => `${pageRoutes.products}?status=${status}`,
  /** The products an import made, optionally one dry-run verdict only. */
  importProducts: (importId: string, verdict?: string) =>
    `${pageRoutes.products}?import=${id(importId)}${verdict ? `&verdict=${id(verdict)}` : ''}`,
};
